import React from 'react';
import { Link } from 'react-router-dom';
import { getResellerSiteUrl } from '../../utils/resellerSiteUrl';
import { FINOS_CATEGORIES, decodeCategories } from '../../utils/categories';
import { longDate, percent, ratePct, rs, shortDate } from '../format';
import DataGrid from '../../components/Common/DataGrid';
import { Async, BarChart, BucketPill, Button, EmptyState, Kpi, KpiGrid, PageHeader, Panel, StatusBar } from '../ui';
import { useDash } from '../DashboardShell';
import { copyText } from '../clipboard';
import { ProofButton } from '../Proof';
import useFetch from '../useFetch';

const ORDER = ['Approved', 'Pending', 'In progress', 'Declined'];

export const SiteLink = () => {
  const { notify } = useDash();
  const { data } = useFetch('/me/profile');
  if (!data) return null;
  const url = getResellerSiteUrl(data.subdomain);
  const copy = async () => notify((await copyText(url)) ? 'Link copied' : 'Could not copy. Select the link and copy it.', 'info');
  return (
    <div className="ds-linkbox">
      <span>{getResellerSiteUrl(data.subdomain, { withProtocol: false })}</span>
      <Button size="sm" variant="primary" onClick={copy}>Copy link</Button>
      <a className="ds-btn is-sm" href={url} target="_blank" rel="noreferrer">Open site</a>
    </div>
  );
};

const Overview = () => {
  const state = useFetch('/me/overview');
  return (
    <Async state={state}>
      {(o) => {
        const t = o.totals;
        const firstName = (o.profile.name || '').split(/\s+/)[0] || 'there';
        const series = o.monthly.map((m, i, all) => ({ label: m.label, a: m.earned, highlight: i === all.length - 1 ? '#1f7a5c' : undefined }));
        const done = o.checklist.filter((s) => s.done).length;
        const cats = decodeCategories(o.profile.market_focus);
        const catLabels = cats.map((id) => FINOS_CATEGORIES.find((c) => c.id === id)?.label || id);
        const decided = o.customer_status.Approved + o.customer_status.Declined;
        return (
          <>
            <PageHeader eyebrow={`Partner since ${longDate(o.profile.created_at)}`} title={`Welcome back, ${firstName}`}>
              <SiteLink />
            </PageHeader>

            <KpiGrid>
              <Kpi label="Approved conversions" value={t.conversions} note="All time" />
              <Kpi label="Commission earned" value={rs(t.commission_earned)} note="All time" />
              <Kpi label="Paid to you" value={rs(t.commission_paid)} note={`${t.payments_count} payment${t.payments_count === 1 ? '' : 's'}`} />
              <Kpi tone="accent" label="Waiting to be paid" value={rs(t.commission_balance)} note="Earned minus paid" />
              <Kpi label="Your rate" value={ratePct(t.commission_rate)} note="Of each approved amount" />
            </KpiGrid>

            <div className="ds-row">
              <Panel wide title="Your commission by month" subtitle="Rs earned from approved applications, last 6 months">
                <BarChart
                  series={series}
                  valueLabels
                  ariaLabel={`Commission per month: ${o.monthly.map((m) => `${m.label} ${m.earned}`).join(', ')}`}
                />
              </Panel>
              <Panel title="Your customers by status" subtitle={t.customers ? `${t.customers} application${t.customers === 1 ? '' : 's'} sent${t.approval_rate !== null ? `, ${percent(t.approval_rate)} of decided ones approved` : ''}` : 'No applications yet'}>
                <StatusBar counts={o.customer_status} order={ORDER} />
                {decided === 0 && t.customers > 0 && <p className="ds-foot">Nothing has been decided yet, so there is no approval rate.</p>}
                <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #e6ecf3' }}>
                  <p className="ds-eyebrow" style={{ fontWeight: 700, letterSpacing: 0.7, textTransform: 'uppercase', fontSize: 12 }}>Your categories</p>
                  <div className="ds-chips" style={{ marginTop: 8 }}>
                    {catLabels.map((l) => <span key={l} className="ds-chip">{l}</span>)}
                  </div>
                </div>
              </Panel>
            </div>

            <div className="ds-row is-top">
              <Panel wide title="Latest customers" action={<Link className="ds-btn is-sm" to="customers">View all</Link>}>
                {o.recent_customers.length === 0 ? (
                  <EmptyState title="No customers yet">Share your link. Applications from your site show up here.</EmptyState>
                ) : (
                  <DataGrid
                    simple
                    rows={o.recent_customers}
                    columns={[
                      { title: 'Customer', render: (c) => <><strong>{c.name}</strong><small>{c.application_id}</small></>, value: (c) => c.name },
                      { title: 'Product', render: (c) => c.product, value: (c) => c.product },
                      { title: 'Status', render: (c) => <BucketPill bucket={c.bucket} label={c.status} />, value: (c) => c.status },
                      { title: 'Date', numeric: true, render: (c) => shortDate(c.date), value: (c) => c.date || '' },
                      { title: 'Your commission', numeric: true, render: (c) => <strong>{c.commission > 0 ? rs(c.commission) : '–'}</strong>, value: (c) => c.commission },
                    ]}
                  />
                )}
              </Panel>

              <div className="ds-col">
                <Panel title="Set up your account" action={<strong style={{ fontSize: 13 }}>{done} of {o.checklist.length}</strong>}>
                  <div className="ds-progress" role="img" aria-label={`${done} of ${o.checklist.length} steps complete`}>
                    {o.checklist.map((s) => <span key={s.key} className={s.done ? 'on' : ''} />)}
                  </div>
                  <ul className="ds-steps">
                    {o.checklist.map((s) => (
                      <li key={s.key} className={s.done ? 'is-done' : ''}>
                        <span className="ds-tick">{s.done && <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5 9-10" /></svg>}</span>
                        {s.label}
                      </li>
                    ))}
                  </ul>
                  {done < o.checklist.length && (
                    <Link className="ds-btn ds-btn-dark" to="settings" style={{ marginTop: 14, width: '100%' }}>
                      {o.checklist.some((s) => s.key === 'bank' && !s.done) ? 'Add bank details' : 'Finish setup'}
                    </Link>
                  )}
                  {o.checklist.some((s) => s.key === 'bank' && !s.done) && <p className="ds-foot">We need bank details before we can send your next payout.</p>}
                </Panel>

                <Panel title="Payments to you" action={<Link className="ds-btn is-sm" to="earnings">History</Link>}>
                  {o.payouts.length === 0 ? (
                    <p className="ds-foot">No payments yet. Payouts you receive are listed here.</p>
                  ) : (
                    <ul className="ds-steps" style={{ gap: 0 }}>
                      {o.payouts.map((p) => (
                        <li key={p.id} style={{ justifyContent: 'space-between', padding: '12px 0', borderTop: '1px solid #eef2f8', alignItems: 'flex-start' }}>
                          <div>
                            <strong style={{ fontSize: 14 }}>{rs(p.amount)}</strong>
                            <small style={{ display: 'block', color: '#5a6a7e', fontWeight: 400 }}>{p.reference || 'Bank transfer'}</small>
                            {p.has_proof && <ProofButton url={`/me/payouts/${p.id}/proof`} label="View receipt" />}
                          </div>
                          <span style={{ fontSize: 12, color: '#5a6a7e', fontWeight: 400 }}>{shortDate(p.paid_at)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Panel>
              </div>
            </div>
          </>
        );
      }}
    </Async>
  );
};

export default Overview;
